<template>
    <ObjectiveSectionLayout>
        <CompassIcon slot="icon" size="large" icon="obj-nb-green-3" />
        <template slot="title">
            {{ $l10n.__('Quelles sont vos attentes ?') }}
        </template>

        <div class="objective-result__previous-perform">
            <img src="/ressources/logged/img/objectives/objective-result.svg" />

            <div class="objective-result__previous-perform--amount">
                <span class="typographies-paragraph-x-large-semibold amount">
                    <template v-if="hasPreviousRefinements">
                        {{
                            previousTotalAmount
                                | numberformatCurrencyCustomNumberFormat({
                                    currency: settings.currency,
                                    currencysymbol: currencySymbol,
                                })
                        }}
                    </template>
                    <template v-else> - </template>
                </span>
                <span class="typographies-paragraph-small-regular">
                    {{
                        $l10n.__(
                            hasPreviousRefinements ? 'Réalisé en %s' : 'Aucune donnée à afficher',
                            previousYear,
                        )
                    }}
                </span>
            </div>
        </div>

        <div class="objective-result__expected-amount">
            <CompassFormItem
                :label="$t.__('objectives.create.section.ObjectiveSectionResult.expected_result')"
                class="objective-result__expected-amount--estimation"
                :error="errors && errors.get('turnover.amount')"
            >
                <AmountInputFormated
                    v-model="expectedAmount"
                    :currencySymbol="currencySymbol"
                    :disabled="isRefinementsAdjustable"
                    :placeholder="
                        $t.__(
                            'objectives.create.section.ObjectiveSectionResult.enter_expected_amount',
                        )
                    "
                    :error="errors && errors.has('turnover.amount')"
                />
            </CompassFormItem>

            <div v-if="canAdjustRefinements" class="objective-result__expected-amount--buttons">
                <CompassSwitch
                    v-model="isRefinementsAdjustable"
                    class="objective-result__expected-amount--buttons-toggle"
                    :label="
                        $t.__(
                            'objectives.create.section.ObjectiveSectionResult.adjust_monthly_payment',
                        )
                    "
                    orientation="left"
                    :disabled="!enabledSwitchButton"
                    @change="adjustRefinements()"
                />
                <CompassPlainButton
                    v-if="hasRefinements"
                    :label="$t.__('common.actions.edit')"
                    class="objective-result__expected-amount--buttons-edit"
                    @click="editRefinements()"
                />
            </div>
        </div>

        <div
            v-if="isRefinementsAdjustable && isRefinementsDisplayed"
            class="objective-result__expected-perform typographies-paragraph-medium-regular"
        >
            <div>
                {{
                    $t.__(
                        'objectives.create.section.ObjectiveSectionResult.adjusted_expected_result',
                    )
                }}
            </div>

            <div class="objective-result__expected-perform--amounts">
                <div class="typographies-paragraph-x-large-semibold">
                    <CompassLoader v-if="isNewExpectedAmountLoading" />
                    <span v-else class="objective-result__expected-perform--amounts-new">
                        {{
                            newExpectedAmount
                                | numberformatCurrencyCustomNumberFormat({
                                    currency: settings.currency,
                                    currencysymbol: currencySymbol,
                                })
                        }}
                    </span>
                </div>

                <div
                    v-if="hasPreviousRefinements"
                    class="objective-result__expected-perform--amounts-previous typographies-paragraph-small-regular"
                >
                    <span class="amount">
                        {{
                            previousTotalAmount
                                | numberformatCurrencyCustomNumberFormat({
                                    currency: settings.currency,
                                    currencysymbol: currencySymbol,
                                })
                        }}
                    </span>
                    <span class="year">
                        {{ previousYear }}
                    </span>
                </div>
            </div>
        </div>

        <div
            v-show="canDisplayRefinements"
            ref="objectiveResultRefinementsCalendar"
            class="objective-result__refinements-calendar"
        >
            <div
                v-for="newRefinement in newRefinements"
                :key="`refinement-${newRefinement.label}`"
                class="objective-result__refinements-calendar--refinement"
            >
                <CompassFormItem :label="newRefinement.label">
                    <AmountInputFormated
                        v-model="newRefinement.amount"
                        :currencySymbol="currencySymbol"
                        :placeholder="
                            $t.__(
                                'objectives.create.section.ObjectiveSectionResult.enter_monthly_payment',
                            )
                        "
                        @input="enableNewExpectedAmountLoader()"
                    />
                </CompassFormItem>

                <div
                    v-if="canDisplayPreviousObjectiveOfARefinement(newRefinement)"
                    class="objective-result__refinements-calendar--refinement-previous typographies-paragraph-small-regular"
                >
                    <span class="amount">
                        {{
                            newRefinement.previousObjective.amount
                                | numberformatCurrencyCustomNumberFormat({
                                    currency: settings.currency,
                                    currencysymbol: currencySymbol,
                                })
                        }}
                    </span>
                    <span class="period">
                        {{ newRefinement.previousObjective.period }}
                    </span>
                </div>
            </div>
        </div>

        <div v-if="canDisplayRefinements" class="objective-result__buttons">
            <CompassButton
                :label="$t.__('common.actions.cancel')"
                type="secondary"
                class="objective-result__buttons--cancel"
                :disabled="isFirstRefinementAdjust"
                @click="cancelEditedRefinements()"
            >
            </CompassButton>
            <CompassButton
                :label="$t.__('common.actions.save')"
                class="objective-result__buttons--save"
                @click="saveRefinements()"
            >
            </CompassButton>
        </div>
    </ObjectiveSectionLayout>
</template>

<script>
import MonthsConfig from 'Config/authenticated/Months';
import ObjectiveSectionLayout from './ObjectiveSectionLayout.vue';
import ObjectiveSectionMixin from './ObjectiveSectionMixin';
import AmountInputFormated from 'vcomponents/inputs/AmountInputFormated.vue';
import { createNamespacedHelpers } from 'vuex';
const corpMapState = createNamespacedHelpers('corp').mapState;

export default {
    components: {
        ObjectiveSectionLayout,
        AmountInputFormated,
    },
    mixins: [ObjectiveSectionMixin],
    props: {
        selectedDuration: {
            type: Object,
            required: true,
        },
        periodDateFormat: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isFirstRefinementAdjust: true,
            isNewExpectedAmountLoading: false,
            isRefinementsAdjustable: false,
            isRefinementsDisplayed: false,
            selectedQuarter: moment().quarter(),
            expectedAmountValue: 0,
            refinements: [],
            newRefinements: [],
            monthsAvailable: MonthsConfig,
        };
    },
    events: {
        'objectiveSectionResult:updateQuarterDuration': 'updateQuarterDuration',
    },
    computed: {
        ...corpMapState(['numformat']),
        expectedAmount: {
            get() {
                return this.expectedAmountValue;
            },
            set(val) {
                this.expectedAmountValue = val;
                this.updateFormResult();
            },
        },
        hasPreviousRefinements() {
            return this.previousTotalAmount !== 0 && this.previousYear !== '';
        },
        hasNewRefinements() {
            return this.newRefinements.length > 0;
        },
        hasRefinements() {
            return this.refinements.length > 0;
        },
        canAdjustRefinements() {
            return this.selectedDuration.duration !== 'month';
        },
        canDisplayRefinements() {
            return (
                this.isRefinementsAdjustable &&
                this.hasNewRefinements &&
                this.isRefinementsDisplayed
            );
        },
        enabledSwitchButton() {
            return (
                this.expectedAmount !== 0 &&
                this.expectedAmount !== '' &&
                moment(this.selectedDuration['date_start']).isValid()
            );
        },
        newExpectedAmount() {
            let newExpectedAmount = this.expectedAmount;
            if (this.isRefinementsAdjustable) {
                newExpectedAmount = 0;
                this.newRefinements.forEach((newRefinement) => {
                    newExpectedAmount = parseFloat(
                        (newExpectedAmount + newRefinement.amount).toFixed(2),
                    );
                });
            }

            return newExpectedAmount;
        },
        previousTotalAmount() {
            let totalPreviousAmount = 0;
            const selectedDateStartFormated = moment(this.selectedDuration.date_start)
                .subtract(1, 'year')
                .format(this.periodDateFormat);
            const selectedDateEndFormated = moment(this.selectedDuration.date_end)
                .subtract(1, 'year')
                .format(this.periodDateFormat);

            for (const previousRefinement of this.settings.previousRefinements) {
                if (
                    previousRefinement.period.date_start === selectedDateStartFormated &&
                    previousRefinement.period.date_end === selectedDateEndFormated
                ) {
                    totalPreviousAmount = previousRefinement.turnover.amount;
                }
            }

            return totalPreviousAmount;
        },
        previousYear() {
            return moment(
                this.settings.previousRefinements[this.settings.previousRefinements.length - 1]
                    ?.period.date_start,
            ).year();
        },
        quarterMonthsAvailable() {
            return this.monthsAvailable.filter(
                (monthAvailable) => monthAvailable.quarter === this.selectedQuarter,
            );
        },
        yearlyMonthsAvailable() {
            let monthsAvailbleFormatted = this.monthsAvailable;
            const startMonth = moment(this.selectedDuration.date_start).format('MM');
            const indexStartMonth = monthsAvailbleFormatted.findIndex(
                (monthAvailable) => monthAvailable.month === startMonth,
            );
            const previousMonths = monthsAvailbleFormatted.slice(0, indexStartMonth);
            const nextMonths = monthsAvailbleFormatted.slice(indexStartMonth);
            monthsAvailbleFormatted = nextMonths.concat(previousMonths);

            return monthsAvailbleFormatted;
        },
        currencySymbol() {
            return (
                window.Currency.getCurrencySymbol(this.settings.currency) ??
                window.App.numformat.currencysymbol
            );
        },
    },
    mounted() {
        this.expectedAmountValue = this.value.turnover.amount;

        if (this.value.refinements.length > 0) {
            this.setRefinements(this.value.refinements);
            this.isRefinementsAdjustable = true;
        }
    },
    methods: {
        adjustRefinements() {
            if (this.isRefinementsAdjustable) {
                this.setNewRefinements();
                this.isRefinementsDisplayed = true;
                this.scrollToRefinements();
            } else {
                this.resetRefinements();
                this.isRefinementsDisplayed = false;
            }
        },
        editRefinements() {
            this.isRefinementsDisplayed = true;
            this.scrollToRefinements();
        },
        setNewRefinements() {
            this.newRefinements = [];
            switch (this.selectedDuration.duration) {
                case 'year':
                case 'fiscal_year':
                    this.setYearlyRefinements();
                    break;
                case 'quarter':
                    this.setQuarterlyRefinements();
                    break;
            }
        },
        setNewRefinementsWithSavedRefinements() {
            this.newRefinements = [];
            this.newRefinements = this.refinements.map((refinement) => ({ ...refinement }));
        },
        setYearlyRefinements() {
            const totalMonthsPerYear = 12;
            const totalMonthsPerYearWithoutFirstMonth = 11;
            const refinementAmounts = this.getRefinementAmounts(
                totalMonthsPerYear,
                totalMonthsPerYearWithoutFirstMonth,
            );

            this.updateRefinements(this.yearlyMonthsAvailable, refinementAmounts);
        },
        setQuarterlyRefinements() {
            const totalMonthsPerQuarter = 3;
            const totalMonthsPerQuarterWithoutFirstMonth = 2;
            const refinementAmounts = this.getRefinementAmounts(
                totalMonthsPerQuarter,
                totalMonthsPerQuarterWithoutFirstMonth,
            );

            this.updateRefinements(this.quarterMonthsAvailable, refinementAmounts);
        },
        getRefinementAmounts(totalMonths, totalMonthsWithoutFirstMonth) {
            const refinementAmount = this.expectedAmount / totalMonths;
            const refinementAmountRounded = Math.floor(refinementAmount);
            const firstRefinementAmount =
                this.expectedAmount - refinementAmountRounded * totalMonthsWithoutFirstMonth;

            return {
                refinementAmount: refinementAmountRounded,
                firstRefinementAmount: firstRefinementAmount,
            };
        },
        updateRefinements(monthsAvailable, refinementAmounts) {
            monthsAvailable.forEach((monthAvailable, index) => {
                const previousRefinement = this.getPreviousRefinement(monthAvailable);

                this.newRefinements.push({
                    label: monthAvailable.label,
                    month: monthAvailable.month,
                    amount:
                        index === 0
                            ? refinementAmounts.firstRefinementAmount
                            : refinementAmounts.refinementAmount,
                    previousObjective:
                        previousRefinement !== undefined
                            ? {
                                  amount: previousRefinement.turnover.amount,
                                  period: `${monthAvailable.labelMinified} ${moment(
                                      previousRefinement.period.date_start,
                                  ).year()}`,
                              }
                            : null,
                });
            });
        },
        setRefinements(refinements) {
            for (const refinement of refinements) {
                const refinementMonth = moment(refinement.period.date_start).format('MM');
                const refinementMonthAvailable = this.monthsAvailable.find(
                    (monthAvailable) => monthAvailable.month === refinementMonth,
                );
                const previousRefinement = this.getPreviousRefinement(refinementMonthAvailable);

                this.refinements.push({
                    label: refinementMonthAvailable.label,
                    month: refinementMonthAvailable.month,
                    amount: refinement.turnover.amount,
                    previousObjective:
                        previousRefinement !== undefined
                            ? {
                                  amount: previousRefinement.turnover.amount,
                                  period: `${refinementMonthAvailable.labelMinified} ${moment(
                                      previousRefinement.period.date_start,
                                  ).year()}`,
                              }
                            : null,
                });
            }

            this.setNewRefinementsWithSavedRefinements();
        },
        updateQuarterDuration(data) {
            if (data?.params && data.params[0]) {
                this.selectedQuarter = data.params[0];
                if (this.hasRefinements) {
                    this.updateQuarterlyRefinements();
                    this.saveRefinements(this.isRefinementsDisplayed);
                } else {
                    this.setNewRefinements();
                }
            }
        },
        updateQuarterlyRefinements() {
            this.newRefinements = [];
            const refinementAmounts = this.refinements.map((refinement) => refinement.amount);

            this.quarterMonthsAvailable.forEach((monthAvailable, index) => {
                const previousRefinement = this.getPreviousRefinement(monthAvailable);

                this.newRefinements.push({
                    label: monthAvailable.label,
                    month: monthAvailable.month,
                    amount: refinementAmounts[index],
                    previousObjective:
                        previousRefinement !== undefined
                            ? {
                                  amount: previousRefinement.turnover.amount,
                                  period: `${monthAvailable.labelMinified} ${moment(
                                      previousRefinement.period.date_start,
                                  ).year()}`,
                              }
                            : null,
                });
            });
        },
        getPreviousRefinement(monthAvailable) {
            //`previousRefinement` can be `undefined` if no previous refinement exist for the current month.
            //If so, don't display previous refinement block under the current refinement field.
            return this.settings.previousRefinements.find(
                (previousRefinement) =>
                    parseInt(monthAvailable.month) - 1 ===
                    moment(previousRefinement.period.date_start).month(),
            );
        },
        enableNewExpectedAmountLoader() {
            if (this.isFirstRefinementAdjust) {
                this.isFirstRefinementAdjust = false;
                this.isNewExpectedAmountLoading = true;

                setTimeout(() => {
                    this.isNewExpectedAmountLoading = false;
                }, 1000);
            }
        },
        resetRefinements() {
            this.refinements = [];
            this.newRefinements = [];
            this.updateFormResult();
            this.isFirstRefinementAdjust = true;
        },
        saveRefinements(newIsRefinementsDisplayed) {
            this.refinements = this.newRefinements.map((newRefinement) => ({ ...newRefinement }));
            this.expectedAmount = this.newExpectedAmount;
            this.isRefinementsDisplayed = newIsRefinementsDisplayed ?? false;
            this.isFirstRefinementAdjust = true;
            this.$pushToast({
                type: 'success',
                icon: true,
                text: this.$l10n.__('Les mensualités ont été ajustées avec succès'),
            });
        },
        updateFormResult() {
            const formResultDatas = {
                turnover: {
                    amount: this.expectedAmount,
                    currency: this.settings.currency,
                },
                refinements: [],
            };

            this.refinements.forEach((monthlyPayment) => {
                const currentStartDate = moment(
                    `${moment(this.selectedDuration['date_start']).year()}-${
                        monthlyPayment.month
                    }-01`,
                ).format();
                const currentEndDate = moment(currentStartDate).endOf('month').format();

                formResultDatas.refinements.push({
                    period: {
                        duration: 'month',
                        date_start: currentStartDate,
                        date_end: currentEndDate,
                    },
                    turnover: {
                        amount: monthlyPayment.amount,
                        currency: this.settings.currency,
                    },
                });
            });

            this.$emit('input', formResultDatas);
        },
        canDisplayPreviousObjectiveOfARefinement(refinement) {
            return this.hasPreviousRefinements && refinement.previousObjective;
        },
        scrollToRefinements() {
            this.$nextTick(() => {
                if (this.isRefinementsAdjustable && this.hasNewRefinements) {
                    this.$refs.objectiveResultRefinementsCalendar.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                        inline: 'end',
                    });
                }
            });
        },
        cancelEditedRefinements() {
            if (this.hasRefinements) {
                this.setNewRefinementsWithSavedRefinements();
            } else {
                this.setNewRefinements();
            }
            this.isFirstRefinementAdjust = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.objective-result {
    &__previous-perform {
        display: flex;
        gap: var(--spacing-5);
        align-items: center;
        justify-content: center;

        width: 100%;
        height: 76px;
        margin-bottom: var(--spacing-3);

        background-color: var(--colors-common-background-subtle);
        border-radius: var(--spacing-0);

        &--amount {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-0);

            .amount {
                color: var(--colors-common-text-standard-active);
            }
        }
    }

    &__expected-amount {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-4);
        align-items: flex-end;

        margin-bottom: var(--spacing-3);

        &--buttons {
            display: flex;
            flex-direction: row;
            gap: var(--spacing-3);
            align-items: center;

            &-toggle {
                padding: var(--spacing-1) 0;
            }
        }
    }

    &__expected-perform {
        display: flex;
        align-items: center;
        justify-content: space-between;

        width: 100%;
        margin-bottom: var(--spacing-4);
        padding: var(--spacing-3);

        background-color: var(--colors-common-background-subtle);
        border-radius: 4px;

        &--amounts {
            display: flex;
            gap: var(--spacing-2);
            align-items: center;
            justify-content: space-between;

            &-new {
                color: var(--colors-common-text-primary-default);
            }

            &-previous {
                display: flex;
                gap: var(--spacing-2);
                align-items: center;
                justify-content: space-between;

                padding: var(--spacing-0) var(--spacing-2);

                color: var(--colors-common-text-standard-caption);

                border: 1px solid var(--colors-common-border-default);
                border-radius: 4px;

                .amount {
                    font-weight: var(--font-weights-proxima-nova-0);
                }
            }
        }
    }

    &__refinements-calendar {
        display: flex;
        flex-flow: row wrap;
        gap: var(--spacing-1) var(--spacing-4);
        align-items: flex-start;
        justify-content: space-between;

        margin-bottom: var(--spacing-4);

        &--refinement {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-0);
            align-items: stretch;

            width: calc((100% / 3) - ((2 / 3) * var(--spacing-4)));

            &-previous {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;

                width: 100%;
                padding: var(--spacing-0) var(--spacing-2);

                color: var(--colors-common-text-standard-caption);

                background-color: var(--colors-common-background-subtle);
                border-radius: 4px;

                .amount {
                    font-weight: var(--font-weights-proxima-nova-0);
                }

                .period {
                    text-align: right;
                }
            }
        }
    }

    &__buttons {
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-end;
    }
}
</style>

<style lang="scss">
.objective-result {
    &__estimation {
        .compass-icon {
            color: var(--colors-common-text-standard-caption);
        }
    }
}
</style>
